import { useMemo } from 'react';
import { GeneralSettingsType } from '../SettingsSidebar/types';

export const useHardwareSensorManager = (settings: GeneralSettingsType) => {
  // NEXMO SENSORS

  // Calculate the maximum number of nexmo sensors allowed
  const maxNexmoSensors = useMemo(() => {
    const numberOfXN185 = settings.controllers.filter(
      (c) => c.type === 'XN185',
    ).length;
    const numberOfEM8 = settings.controllers.filter(
      (c) => c.type === 'XM350-EM8',
    ).length;
    return (numberOfXN185 + numberOfEM8) * 8; // Each controller supports up to 8 sensors
  }, [settings.controllers]);

  // Calculate the total number of registered nexmo sensors
  const totalNexmoSensors = useMemo(() => {
    return (
      settings.nfcReaders.filter((reader) => reader.type === 'Nexmo-NFC')
        .length +
      settings.buttons.length +
      settings.buzzers.length
    );
  }, [settings.nfcReaders, settings.buttons, settings.buzzers]);

  // TH SENSORS

  const maxTHSensors = useMemo(() => {
    const numberOfTH4x = settings.controllers.filter(
      (c) => c.type === 'TH_LINK_4x',
    ).length;
    const numberOfTH8x = settings.controllers.filter(
      (c) => c.type === 'TH_LINK_8x',
    ).length;
    return numberOfTH4x * 4 + numberOfTH8x * 8;
  }, [settings.controllers]);

  const totalTHSensors = useMemo(
    () =>
      settings.nfcReaders.filter((reader) => reader.type === 'TH-NFC').length,
    [settings.nfcReaders],
  );

  const moreTHSensorsAllowed = useMemo(
    () => totalTHSensors < maxTHSensors,
    [totalTHSensors, maxTHSensors],
  );

  const moreNexmoSensorsAllowed = useMemo(
    () => totalNexmoSensors < maxNexmoSensors,
    [totalNexmoSensors, maxNexmoSensors],
  );

  // LEDs
  const maxLEDs = useMemo(() => {
    return (
      settings.controllers.filter(
        (controller) => controller.type === 'XM350-EM6',
      ).length *
        5 +
      1 // Each controller supports up to 5 LEDs (+ 1 to allow for all LEDs option (option X))
    );
  }, [settings.controllers]);

  const totalLEDs = useMemo(() => {
    return settings.leds.length;
  }, [settings.leds]);

  const moreLEDsAllowed = useMemo(
    () => totalLEDs < maxLEDs,
    [totalLEDs, maxLEDs],
  );

  return { moreNexmoSensorsAllowed, moreTHSensorsAllowed, moreLEDsAllowed };
};
